import {
  ErrorsInstrumentation,
  initializeFaro,
  LogLevel,
  TransportItemType,
  type TransportItem,
} from '@grafana/faro-web-sdk';
// Share the browser-safe scrubber with server logging, without importing the
// core barrel (which loads Node-only logging and tracing dependencies).
import { extractSubdomainOwner, getSubdomainZone } from './subdomain';
import { scrubDiagnosticValue } from '../../../core/src/utils/diagnostic-scrubber';

let telemetry: ReturnType<typeof initializeFaro> | undefined;

function isHostedTenant(host: string): boolean {
  return host !== 'staging.lobu.ai' && extractSubdomainOwner(host, 'lobu.ai') !== null;
}

export function scrubTelemetryItem(item: TransportItem): TransportItem | null {
  // Error investigation needs release and stack, not browsing/session/user
  // metadata. Never include ambient page URLs or console breadcrumbs.
  if (item.type !== TransportItemType.EXCEPTION && item.type !== TransportItemType.LOG) return null;
  const payload = { ...item.payload };
  if ('context' in payload && payload.context) {
    // Faro serializes structured causes/context before beforeSend. Decode them
    // so credential keys remain visible to the shared scrubber.
    payload.context = Object.fromEntries(
      Object.entries(payload.context).map(([key, value]) => {
        try {
          return [key, JSON.stringify(scrubDiagnosticValue(JSON.parse(value)))];
        } catch {
          return [key, value];
        }
      }),
    );
  }
  const scrubbed = scrubDiagnosticValue({
    type: item.type,
    meta: { app: item.meta.app },
    payload,
  }) as TransportItem;
  // Normalize the serialized diagnostic once: Faro stores URLs in exception
  // text, frames and JSON-encoded causes. All tenant SPAs share these assets.
  const body = JSON.stringify(scrubbed).replace(/https?:\/\/[a-z0-9.-]+(?::\d+)?/gi, (origin) => {
    try {
      return isHostedTenant(new URL(origin).hostname.replace(/\.$/, ''))
        ? 'https://app.lobu.ai' : origin;
    } catch { return origin; }
  });
  // Bound each item independently so a batch fits the collector's 64 KiB cap.
  if (new TextEncoder().encode(body).length > 12_000) {
    return {
      type: TransportItemType.LOG,
      meta: scrubbed.meta,
      payload: {
        timestamp: new Date().toISOString(), level: LogLevel.ERROR,
        message: 'Oversized client error; diagnostic details omitted',
        context: { source: 'telemetry', original_type: item.type },
      },
    };
  }
  return JSON.parse(body) as TransportItem;
}

export function initTelemetry(): void {
  const url = import.meta.env.VITE_FARO_URL;
  if (telemetry || !url || !import.meta.env.PROD) return;
  // A production bundle opened by a local preview must not report as prod.
  const host = window.location.hostname;
  let collector: URL;
  try {
    collector = new URL(url);
  } catch {
    // Telemetry configuration must not prevent the application from mounting.
    return;
  }
  // The release image also runs in staging. Only the production app may send
  // to the production collector; custom installations supply their own URL.
  const productionTenant = getSubdomainZone() === 'lobu.ai' &&
    isHostedTenant(host);
  if (collector.hostname === 'telemetry.lobu.ai' &&
      host !== 'app.lobu.ai' && host !== 'summaries.now' && !productionTenant) return;
  if (host === 'localhost' || host.endsWith('.local') || host.endsWith('.ts.net') ||
      host === '[::1]' || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)) return;
  telemetry = initializeFaro({
    url,
    app: {
      name: 'lobu-web',
      environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      release: import.meta.env.VITE_APP_GIT_SHA || undefined,
    },
    instrumentations: [new ErrorsInstrumentation()],
    beforeSend: scrubTelemetryItem,
    batching: { enabled: true, itemLimit: 3, sendTimeout: 1000 },
  });
}

export function reportTelemetryError(error: unknown, context: Record<string, string>): void {
  telemetry?.api.pushError(error instanceof Error ? error : new Error(String(error)), { context });
}

export function reportTelemetryLog(message: string, level: 'error' | 'warning'): void {
  telemetry?.api.pushLog([message], { level: level === 'error' ? LogLevel.ERROR : LogLevel.WARN });
}
